import { useState, useEffect } from 'react'
import { Select, Modal, Badge } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { apiRequest } from '../../utils/api'
import { useAuthStore } from '../../store'
import BaseButton from '../common/BaseButton'
import List from '../common/List'
import styles from '../../styles/admin/Main.module.scss'
import { Pagination } from '@mantine/core'

const PromoCodesBox = () => {
  const { user } = useAuthStore()
  const [codes, setCodes] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [duration, setDuration] = useState('1')
  const [email, setEmail] = useState('')
  const [creating, setCreating] = useState(false)
  const [newCode, setNewCode] = useState(null)
  const [emailResult, setEmailResult] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const headers = ['Code', 'Duration', 'Status', 'Used By', 'Used At', 'Created At', '']
  const columnWidths = ['19%', '11%', '10%', '18%', '18%', '16%', '8%']

  useEffect(() => {
    fetchCodes()
  }, [])

  const fetchCodes = async () => {
    try {
      const res = await apiRequest('/admin/promoCode/list', 'POST', {})
      if (res.success) {
        setCodes(res.data)
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to load promo codes.', color: 'red' })
    }
  }

  const handleCreate = async () => {
    setCreating(true)
    setNewCode(null)
    setEmailResult(null)
    try {
      const res = await apiRequest('/admin/promoCode/create', 'POST', {
        DURATION_MONTHS: Number(duration),
        CREATED_BY: user.USER_ID,
        EMAIL: email.trim() || undefined,
      })
      if (res.success) {
        setNewCode(res.code)
        await fetchCodes()
        if (res.emailSent) {
          setEmailResult('sent')
          notifications.show({ title: 'Created', message: `Code ${res.code} generated and emailed to ${email}.`, color: 'green' })
        } else if (res.emailError) {
          setEmailResult('error')
          notifications.show({ title: 'Created', message: `Code ${res.code} generated, but email sending failed.`, color: 'yellow' })
        } else {
          notifications.show({ title: 'Created', message: `Code ${res.code} generated.`, color: 'green' })
        }
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to create code.', color: 'red' })
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promo code?')) return
    try {
      const res = await apiRequest('/admin/promoCode/delete', 'POST', { ID: id })
      if (res.success) {
        await fetchCodes()
        notifications.show({ title: 'Deleted', message: 'Promo code deleted.', color: 'gray' })
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to delete code.', color: 'red' })
    }
  }

  const tableData = codes.map((c) => ({
    Code: c.CODE,
    Duration: `${c.DURATION_MONTHS} month${c.DURATION_MONTHS > 1 ? 's' : ''}`,
    Status: c.IS_USED === 'Y' ? 'Used' : 'Unused',
    'Used By': c.USED_BY_NAME || '-',
    'Used At': c.USED_AT || '-',
    'Created At': c.CREATED_AT,
    '': c.IS_USED === 'N' ? (
      <button
        onClick={(e) => { e.stopPropagation(); handleDelete(c.ID) }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e03131', padding: '2px 6px', fontSize: 15 }}
        title="Delete"
      >🗑</button>
    ) : null,
  }))

  const paginated = tableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  const totalPages = Math.ceil(tableData.length / itemsPerPage)

  return (
    <div>
      <div className="uploads-search-section">
        <div />
        <div className="search-buttons">
          <BaseButton onClick={() => { setIsModalOpen(true); setNewCode(null) }}>
            Generate Code
          </BaseButton>
        </div>
      </div>

      <List
        showCheckbox={false}
        headers={headers}
        data={paginated}
        columnWidths={columnWidths}
      />

      <div className={styles.paginationWrapper}>
        <Pagination
          value={currentPage}
          onChange={setCurrentPage}
          total={totalPages}
          size="sm"
          withEdges={false}
          color="gray"
          classNames={{ control: styles.paginationControl }}
        />
      </div>

      <Modal
        opened={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEmail(''); setEmailResult(null) }}
        title="Generate Promo Code"
        centered
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Select
            label="Duration"
            data={[
              { value: '1', label: '1 Month' },
              { value: '3', label: '3 Months' },
            ]}
            value={duration}
            onChange={(v) => setDuration(v)}
          />

          <div>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#333' }}>
              Email <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>(optional — sends welcome email with code)</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="recipient@example.com"
              style={{
                width: '100%',
                padding: '9px 12px',
                border: '1px solid #ced4da',
                borderRadius: 6,
                fontSize: 14,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <BaseButton onClick={handleCreate} disabled={creating}>
            {creating ? 'Generating...' : 'Generate'}
          </BaseButton>

          {newCode && (
            <div style={{ padding: '14px 16px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #86efac', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 12, color: '#166534' }}>New code generated:</p>
              <p style={{ margin: '6px 0 0', fontSize: 22, fontWeight: 700, letterSpacing: 2, color: '#15803d' }}>{newCode}</p>
              {emailResult === 'sent' && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#166534' }}>✓ Email sent to {email}</p>
              )}
              {emailResult === 'error' && (
                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b45309' }}>⚠ Email sending failed — please share the code manually</p>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default PromoCodesBox
